import React, { useCallback, useMemo } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  Grid,
  Typography,
  Button,
  TextField,
  useMediaQuery,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  Chip,
  ListSubheader,
  MenuItem,
  Box,
} from '@mui/material';
import { useNominateForShabashMutation } from '../shabashApiSlice';
import { useDispatch, useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import { selectCurrentUserEmpCode } from '../../Auth/authSlice';
import { useParams } from 'react-router-dom';
import { FILE_SIZE } from '../../../constants/attachments';
import { variants } from '../../../variants/variants';
import FileUpload from '../../../common/Utility/FileUpload';
import { useEffect } from 'react';
import { useState } from 'react';
import UserAvatar from '../../../common/UserAvatar';
import { useGetAllEmployeesQuery } from '../../RnR/rnrApiSlice';
import { toggleSwipe } from '../../Search/searchSlice';
import AttachmentNameDisplay from '../../../common/Utility/AttachmentNameDisplay';

const validationSchema = Yup.object().shape({
  description: Yup.string().min(10).max(500).required('Description is required'),
  attachment: Yup.array(),
  // .of(Yup.mixed().test("fileSize", "File is too large", value => !value || (value && value.size <= FILE_SIZE))),
});

const ShabashForm = () => {
  const currUserEmpCode = useSelector(selectCurrentUserEmpCode);
  const { empCode } = useParams();
  const [nominateForShabash] = useNominateForShabashMutation();
  const { data: employeeData, isLoading, error } = useGetAllEmployeesQuery();
  const [isAttached, setIsAttached] = useState(false);
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down('sm'));
  const [searchTerm, setSearchTerm] = useState('');
  const [searchText, setSearchText] = useState('');
  const dispatch = useDispatch();
  const formik = useFormik({
    initialValues: {
      description: '',
      attachment: [],
      personName: [],
    },
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      const formData = new FormData();
      const selectedEmp = employeeData.find((emp) => emp.userName === values.personName);
      formData.append('nominationReason', values.description);
      values.attachment.forEach((file) => {
        formData.append('attachments', file);
      });
      formData.append('nominatorEmpCode', currUserEmpCode);
      formData.append('nomineeEmpCode', selectedEmp?.empCode);
      dispatch(toggleSwipe({ value: false }));
      await toast.promise(nominateForShabash(formData).unwrap(), {
        loading: 'Nominating...',
        success: 'Nomination successful!',
        error: (err) => err.data?.message || 'Error in nominating for shabash, Please try again.',
      });

      resetForm();
    },
  });

  const handleFileChange = (event) => {
    const files = Array.from(event.currentTarget.files);
    console.log('files', files);
    if(formik.values.attachment.length + files.length > 20){
      toast.error('You can upload maximum 20 files');
      return;
    }
    if(files.length > 0 && files[0]?.size <= 20*1024*1024 ){      
      formik.setFieldValue('attachment', files);
    }
    else{
      toast.error('File size should be less than 20MB');
    }
  };

  const takePicture = async () => {
    try {
      window['ReactNativeWebView'] &&
        window['ReactNativeWebView']?.postMessage(JSON.stringify({ type: 'uploadShabashFile', value: true, data:{image:20,selectionLimit:5,validTypes:['image/jpeg', 'image/jpg', 'image/png']}}));
    } catch (error) {
      console.error('Camera error:', error);
    }
  };

  const base64ToBlob = (base64, type = 'application/octet-stream') => {
    try {
      const base64WithoutPrefix = base64.startsWith('data:') ? base64.substring(base64.indexOf(',') + 1) : base64;
      const byteCharacters = atob(base64WithoutPrefix);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      return new Blob([byteArray], { type });
    } catch (e) {
      console.error('Error converting base64 to Blob:', e);
      console.error('Problematic base64 snippet:', base64.substring(0, 100) + '...');
      return null;
    }
  };

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setSearchTerm(searchText);
    }, 2000); // Adjust delay (ms) as needed

    return () => clearTimeout(delayDebounce); // Clean up on next keystroke
  }, [searchText]);

  useEffect(() => {
    // Handle messages from React Native
    const handleMessage = (event) => {
      try {
        let message = event.data;
        if (message?.type === 'uploadedShabashFile') {
          console.log('uploadedShabashFile', message.data);
          const newFileObjects = [];
          message.data.forEach((asset) => {
            if (!asset.base64 || !asset.fileName || !asset.type) {
              console.warn('Asset is missing required fields (base64, fileName, or type):', asset);
              return; // Skip this asset
            }
            const blob = base64ToBlob(asset.base64, asset.type);
            if (blob) {
              const file = new File([blob], asset.fileName, {
                type: asset.type,
                lastModified: asset.timestamp ? new Date(asset.timestamp).getTime() : Date.now(),
              });
              newFileObjects.push(file);
            } else {
              console.warn('Could not convert asset to blob:', asset.fileName);
            }
          });
          formik.setFieldValue('attachment', newFileObjects);
          setIsAttached(true);
          window['ReactNativeWebView'] &&
            window['ReactNativeWebView']?.postMessage(
              JSON.stringify({
                type: 'uploadedShabashFile',
                value: 'uploadedShabashFile received',
              })
            );
        }
      } catch (err) {
        window['ReactNativeWebView'] &&
          window['ReactNativeWebView']?.postMessage(
            JSON.stringify({
              type: 'error',
              value: event.data,
              message: err,
            })
          );
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);
  const filteredEmployees = useMemo(() => {
    if (!employeeData?.length) return [];

    // Remove duplicates
    const unique = employeeData.filter((emp, index, self) => index === self.findIndex((e) => e._id === emp._id));

    if (!searchText.trim()) return unique.slice(0, 50); // Show only first 50 initially

    const searchLower = searchText.toLowerCase();
    return unique
      .filter((emp) => {
        const searchString = `${emp.firstName || ''} ${emp.middleName || ''} ${emp.lastName || ''} ${
          emp.userName || ''
        } ${emp.department || ''}`.toLowerCase();
        return searchString.includes(searchLower);
      })
      .slice(0, 30); // Limit search results
  }, [employeeData, searchText]);

  const handleSearchChange = useCallback((e) => {
    setSearchText(e.target.value);
  }, []);

  // Add this new handler to prevent Select's default keyboard behavior
  const handleSearchKeyDown = useCallback((e) => {
    // Prevent Select component from handling these keys
    e.stopPropagation();

    // Allow normal text input behavior
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter') {
      e.preventDefault();
    }
  }, []);

  const handleDelete = (e, userNameToDelete) => {
    const updatedMembers = formik.values.teamMembers.filter((userName) => userName !== userNameToDelete);
    formik.setFieldValue('teamMembers', updatedMembers);
  };

  return (
    <div style={{ padding: '5px', maxWidth: '600px', margin: '0 auto' }}>
      <form onSubmit={formik.handleSubmit}>
        <Grid container spacing={2}>
          {/* <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel id="select-members-label">Select Member*</InputLabel>
              <Select
                labelId="select-members-label"
                id="select-members"
                value={formik.values.personName}
                // onChange={(event) => {
                //   const selectedUserName = event.target.value;
                //   formik.setFieldValue('personName', selectedUserName);
                //   formik.setFieldValue('numberOfMembers', 1); // Always 1 for single select
                // }}
                onChange={(event) => {
                  const selectedUserName = event.target.value;
                  const selectedEmp = employeeData.find((emp) => emp.userName === selectedUserName);

                  formik.setFieldValue('personName', selectedUserName);
                  formik.setFieldValue('selectedEmpCode', selectedEmp?.empCode || '');
                  formik.setFieldValue('numberOfMembers', 1);

                  setSearchText('');
                  setSearchTerm('');
                  // If you want to populate team info here, you can do it too
                  // formik.setFieldValue('teamMembers', selectedEmp.teamMembers || []);
                }}
                input={<OutlinedInput label="Select Member*" />}
                renderValue={(selected) => {
                  const employee = employeeData.find((emp) => emp.userName === selected);
                  return (
                    <Chip
                      key={selected}
                      label={selected}
                      avatar={<UserAvatar size={24} src={employee?.employeePhotoPath} />}
                    />
                  );
                }}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 300,
                      overflowY: 'auto',
                    },
                  },
                }}
              >
                <ListSubheader>
                  <TextField
                    placeholder="Search member..."
                    fullWidth
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key !== 'Escape') {
                        e.stopPropagation();
                      }
                    }}
                    onClick={(e) => e.stopPropagation()}
                    size="small"
                    autoFocus
                  />
                </ListSubheader>

                {filteredEmployees?.map((option) => (
                  <MenuItem
                    key={option.userName}
                    value={option.userName}
                    dense
                    style={{ paddingTop: 4, paddingBottom: 4 }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <UserAvatar size={28} src={option?.employeePhotoPath} />
                      <div style={{ marginLeft: 8 }}>
                        <Typography variant="body2" style={{ fontSize: 13, fontWeight: 500 }}>
                          {`${option.firstName} ${option.middleName} ${option.lastName}`}
                        </Typography>
                        <Typography variant="caption" style={{ fontSize: 11, color: '#666' }}>
                          {option.department}
                        </Typography>
                      </div>
                    </div>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {formik.touched.personName && formik.errors.personName && (
              <Typography variant="body2" color="error">
                {formik.errors.personName}
              </Typography>
            )}
          </Grid> */}
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel id="select-members-label">Select Member*</InputLabel>
              <Select
                labelId="select-members-label"
                id="select-members"
                value={formik.values.personName || ''}
                onChange={(event) => {
                  const selectedUserName = event.target.value;
                  const selectedEmp = employeeData.find((emp) => emp.userName === selectedUserName);

                  formik.setFieldValue('personName', selectedUserName);
                  formik.setFieldValue('selectedEmpCode', selectedEmp?.empCode || '');
                  formik.setFieldValue('numberOfMembers', 1);
                  setSearchText('');
                }}
                input={<OutlinedInput label="Select Member*" />}
                // Add these props to prevent auto-selection
                autoComplete="off"
                disableAutoFocus
                renderValue={(selected) => {
                  if (!selected) return '';
                  const employee = employeeData?.find((emp) => emp.userName === selected);
                  return (
                    <Chip
                      key={selected}
                      label={selected}
                      avatar={<UserAvatar size={24} src={employee?.employeePhotoPath} />}
                    />
                  );
                }}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 350,
                      overflowY: 'auto',
                    },
                  },
                  // Prevent menu from auto-focusing on items
                  // disableAutoFocusItem: true,
                  // // Prevent menu from restoring focus
                  // disableRestoreFocus: true,
                }}
              >
                {/* <ListSubheader> */}
                  <TextField
                    placeholder="Search member..."
                    fullWidth
                    value={searchText}
                    onChange={handleSearchChange}
                    onKeyDown={handleSearchKeyDown} // Use the new handler
                    onClick={(e) => e.stopPropagation()}
                    size="small"
                    autoFocus
                    // Add these props for better focus management
                    InputProps={{
                      autoComplete: 'off',
                    }}
                  />
                {/* </ListSubheader> */}

                {filteredEmployees.map((option) => (
                  <MenuItem
                    key={option._id || option.userName}
                    value={option.userName}
                    dense
                    style={{ paddingTop: 4, paddingBottom: 4 }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <UserAvatar size={28} src={option?.employeePhotoPath} />
                      <Box sx={{ marginLeft: 1 }}>
                        <Typography variant="body2" sx={{ fontSize: 13, fontWeight: 500 }}>
                          {`${option.firstName || ''} ${option.middleName || ''} ${option.lastName || ''}`.trim()}
                        </Typography>
                        <Typography variant="caption" sx={{ fontSize: 11, color: '#666' }}>
                          {option.department}
                        </Typography>
                      </Box>
                    </Box>
                  </MenuItem>
                ))}

                {filteredEmployees.length === 0 && (
                  <MenuItem disabled>
                    <Typography variant="body2" color="textSecondary">
                      No employees found
                    </Typography>
                  </MenuItem>
                )}
              </Select>
            </FormControl>

            {formik.touched.personName && formik.errors.personName && (
              <Typography variant="body2" color="error">
                {formik.errors.personName}
              </Typography>
            )}
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description *"
              name="description"
              type="text"
              variant="outlined"
              multiline
              rows={3}
              value={formik.values.description}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.description && !!formik.errors.description}
              helperText={formik.touched.description && formik.errors.description}
              margin="normal"
              InputLabelProps={{
                style: { fontSize: variants.fontSize.SUB_HEADING },
              }}
              InputProps={{
                placeholder: 'Enter description here...',
                style: { fontSize: variants.fontSize.SUB_HEADING },
              }}
            />
          </Grid>
          <Grid item xs={12}>
            {!isSm ? (
              <FileUpload
                handleFileChange={handleFileChange}
                formik={formik}
                name="attachment"
                accept={'image/jpg,image/jpeg,image/png'}
              />
            ) : (
              <Button variant="contained" onClick={takePicture}>
                Upload File
              </Button>
            )}

          

            {console.log('formik Data:-', formik.values)}
            {isAttached && (
              <AttachmentNameDisplay attachments={formik.values.attachment} />
            )}
          </Grid>
          <Grid item xs={12}>
            <Button
              type="submit"
              variant="contained"
              fullWidth
              sx={{ fontWeight: 'bold', borderRadius: '8px', fontSize: variants.fontSize.TEXT }}
            >
              Nominate
            </Button>
          </Grid>
        </Grid>
      </form>
    </div>
  );
};

export default ShabashForm;
